import React from 'react'
import { useTheme } from '@mui/material/styles'
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Chip,
  useMediaQuery,
} from '@mui/material'
import {
  LocationOn as LocationOnIcon,
  AttachMoney as AttachMoneyIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  AutoAwesome as AutoAwesomeIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { formatSalary, getPracticeTypeString } from './helpers'
import useJobFeedback from './JobFeedback'

// Match tier color mapping
const getMatchTierColor = (matchTier, theme) => {
  switch (matchTier?.toLowerCase()) {
    case 'excellent':
      return {
        bg: theme.palette.success.light,
        color: theme.palette.success.dark,
        border: theme.palette.success.main,
      }
    case 'strong':
      return {
        bg: theme.palette.info.light,
        color: theme.palette.info.dark,
        border: theme.palette.info.main,
      }
    case 'good':
      return {
        bg: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        border: theme.palette.warning.main,
      }
    case 'moderate':
    default:
      return {
        bg: theme.palette.grey[200],
        color: theme.palette.grey[700],
        border: theme.palette.grey[400],
      }
  }
}

const AIJobCard = ({
  rankedJob,
  isActive,
  onClick,
  conversationId,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const job = rankedJob?.job
  const matchTier = rankedJob?.matchTier
  const justification = rankedJob?.justification

  if (!job) return null

  const matchTierColors = getMatchTierColor(matchTier, theme)

  const feedbackElements = useJobFeedback({
    jobId: job.id,
    conversationId,
    chatbotMessageId: rankedJob.chatbotMessageId,
    matchTier,
    feedback: rankedJob.feedback,
  })

  return (
    <Card
      onClick={onClick}
      sx={{
        width: '100%',
        borderRadius: 1,
        border: isActive
          ? `2px solid ${theme.palette.ai.main}`
          : `1px solid ${theme.palette.divider}`,
        boxShadow: isActive ? theme.palette.shadows.medium : theme.palette.shadows.light,
        cursor: 'pointer',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        '&:hover': {
          boxShadow: theme.palette.shadows.medium,
          borderColor: theme.palette.ai.light,
          transition: 'all 0.2s ease-in-out',
          transform: 'translateZ(0)',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1, md: 1.5 }, '&:last-child': { pb: { xs: 1, md: 1.5 } } }}>
        <Stack spacing={{ xs: 0.75, md: 1 }}>
          {/* Match Tier Badge */}
          {matchTier && (
            <Box>
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: '0.9rem !important' }} />}
                label={matchTier}
                size='small'
                sx={{
                  backgroundColor: matchTierColors.bg,
                  color: matchTierColors.color,
                  borderColor: matchTierColors.border,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  height: '24px',
                  '& .MuiChip-icon': {
                    color: matchTierColors.color,
                  },
                }}
              />
            </Box>
          )}

          {/* Job Title and Employer */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant='subtitle1'
                sx={{
                  color: theme.palette.primary.dark,
                  fontWeight: 'medium',
                  mb: { xs: 0.25, md: 0.5 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: { xs: '-webkit-box', md: 'block' },
                  WebkitLineClamp: { xs: 2 },
                  WebkitBoxOrient: { xs: 'vertical' },
                  whiteSpace: { xs: 'normal', md: 'nowrap' },
                  fontSize: { xs: '0.9rem', md: '1rem' },
                }}
              >
                {job.title}
              </Typography>
              {job.employer?.name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                  <BusinessIcon sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }} color='action' />
                  <Typography variant='body2' color='text.secondary' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: { xs: 'normal', md: 'nowrap' }, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                    {job.employer.name}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexWrap: 'wrap' }}>
              {job.practiceType && getPracticeTypeString(job.practiceType) && (
                <Chip
                  label={getPracticeTypeString(job.practiceType)}
                  size='small'
                  variant='outlined'
                  sx={{ height: '24px', fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>

          {/* Recruiter/Contact Information - Hidden on mobile */}
          {(job.employerUser || job.user) && (
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                backgroundColor: theme.palette.grey[50],
                borderRadius: 1,
                p: 1,
                border: `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon fontSize='small' color='action' />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 'medium',
                        color: theme.palette.text.primary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {job.employerUser?.user?.firstName || job.user?.firstName} {job.employerUser?.user?.lastName || job.user?.lastName}
                    </Typography>
                    {job.employerUser?.jobTitle && (
                      <Typography
                        variant='caption'
                        sx={{
                          color: theme.palette.text.secondary,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {job.employerUser.jobTitle}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Contact Information */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 4 }}>
                  {(job.employerUser?.user?.showEmail || job.user?.showEmail) && (job.employerUser?.user?.email || job.user?.email) && (
                    <Typography
                      component='a'
                      href={`mailto:${job.employerUser?.user?.email || job.user?.email}`}
                      variant='body2'
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        fontSize: '0.85rem',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {job.employerUser?.user?.email || job.user?.email}
                    </Typography>
                  )}
                  {(job.employerUser?.user?.showPhone || job.user?.showPhone) && (job.employerUser?.user?.phone || job.user?.phone) && (
                    <Typography
                      component='a'
                      href={`tel:${job.employerUser?.user?.phone || job.user?.phone}`}
                      variant='body2'
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        fontSize: '0.85rem',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {job.employerUser?.user?.phone || job.user?.phone}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          )}

          {/* Location and Salary - Combined Row */}
          {(job.city || job.state) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                <LocationOnIcon sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }} color='action' />
                <Typography variant='body2' sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                  {job.city}{job.city && job.state && ', '}{job.state}
                </Typography>
              </Box>

              {formatSalary(job.salary || job.salaryMin || job.salaryMax) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                  <AttachMoneyIcon sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }} color='action' />
                  <Typography variant='body2' sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, fontWeight: 'medium' }}>
                    {formatSalary(job.salary || job.salaryMin || job.salaryMax)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Specialties and Candidate Type */}
          {(job.specialties?.length > 0 || job.candidateType?.name) && (
            <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'flex-start' }}>
              {/* Specialties Column */}
              {job.specialties?.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant='caption'
                    sx={{ color: theme.palette.text.secondary, display: { xs: 'none', md: 'block' }, mb: 0.5 }}
                  >
                    Specialties
                  </Typography>
                  <Stack direction='row' spacing={0.5} flexWrap='wrap' gap={0.5}>
                    {job.specialties.slice(0, isMobile ? 1 : 2).map(specialty => (
                      <Chip
                        key={specialty?.id}
                        label={specialty?.name}
                        size='small'
                        sx={{ height: { xs: '18px', md: '20px' }, fontSize: { xs: '0.65rem', md: '0.7rem' } }}
                      />
                    ))}
                    {job.specialties.length > 1 && (
                      <Chip
                        label={`+${job.specialties.length - 1}`}
                        size='small'
                        variant='outlined'
                        sx={{ height: { xs: '18px', md: '20px' }, fontSize: { xs: '0.65rem', md: '0.7rem' }, display: { xs: 'inline-flex', md: job.specialties.length > 2 ? 'inline-flex' : 'none' } }}
                      />
                    )}
                  </Stack>
                </Box>
              )}

              {/* Candidate Type Column - Hidden on mobile */}
              {job.candidateType?.name && (
                <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: '40%' }}>
                  <Typography
                    variant='caption'
                    sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}
                  >
                    Position Type
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SchoolIcon fontSize='small' color='action' />
                    <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                      {job.candidateType.name}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Key Benefits - Inline on mobile */}
          {(job.visaSponsorship || job.loanForgiveness || job.pslfCompatible) && (
            <Box>
              <Typography
                variant='caption'
                sx={{ color: theme.palette.text.secondary, display: { xs: 'none', md: 'block' }, mb: 0.5 }}
              >
                Benefits
              </Typography>
              <Stack direction='row' spacing={0.5} flexWrap='wrap' gap={0.5}>
                {job.visaSponsorship && (
                  <Chip
                    label='Visa Sponsorship'
                    size='small'
                    variant='outlined'
                    color='success'
                    sx={{ height: { xs: '18px', md: '20px' }, fontSize: { xs: '0.65rem', md: '0.7rem' } }}
                  />
                )}
                {job.loanForgiveness && (
                  <Chip
                    label='Loan Forgiveness'
                    size='small'
                    variant='outlined'
                    color='success'
                    sx={{ height: { xs: '18px', md: '20px' }, fontSize: { xs: '0.65rem', md: '0.7rem' } }}
                  />
                )}
                {job.pslfCompatible && (
                  <Chip
                    label='PSLF Eligible'
                    size='small'
                    variant='outlined'
                    color='success'
                    sx={{ height: { xs: '18px', md: '20px' }, fontSize: { xs: '0.65rem', md: '0.7rem' } }}
                  />
                )}
              </Stack>
            </Box>
          )}

          {/* AI Justification - Hidden on mobile */}
          {justification && (
            <>
              <Box
                sx={{
                  display: { xs: 'none', md: 'block' },
                  backgroundColor: `${matchTierColors.bg}`,
                  borderRadius: 1,
                  p: 1,
                  borderLeft: `3px solid ${matchTierColors.border}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography
                    variant='caption'
                    sx={{
                      color: matchTierColors.color,
                      fontWeight: 'bold',
                    }}
                  >
                    Why this match
                  </Typography>
                  {feedbackElements.thumbs}
                </Box>
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: '0.85rem',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {justification}
                </Typography>
              </Box>

              {/* Job feedback panel below justification */}
              {feedbackElements.panel}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default AIJobCard
